// Public terms of service. URL: https://jarvis.yourproject.ai/terms
// Standard SaaS ToS — pre-launch / beta posture, no-warranty, you-own-your-data,
// Stripe-billed, Utah-governed. Reviewed against Meta App Review expectations.
// Not legal advice; review with counsel before broad commercial launch.

import { LegalLayout, Section } from './LegalLayout'
import Seo from '@/components/Seo'

export function TermsOfService() {
  return (
    <>
    <Seo title="Terms of Service — Jarvis" description="The terms that govern your use of Jarvis." path="/terms" />
    <LegalLayout
      title="Terms of Service"
      subtitle="The agreement between you and ClearFinOps LLC for use of Jarvis."
      effectiveDate="May 12, 2026"
    >
      <p>
        These Terms of Service (the &ldquo;Terms&rdquo;) are a binding agreement between you
        (&ldquo;you&rdquo;) and <strong>ClearFinOps LLC</strong>, a Utah limited liability
        company based in Lehi, Utah (&ldquo;ClearFinOps,&rdquo; &ldquo;we,&rdquo; or
        &ldquo;us&rdquo;), governing your access to and use of the Jarvis service offered under
        the yourproject.ai brand (the &ldquo;Service&rdquo;). By creating an account or using the
        Service you agree to be bound by these Terms.
      </p>

      <Section heading="1. The Service">
        <p>
          Jarvis is an AI &ldquo;chief of staff&rdquo; for small-business operators. It
          synchronizes data from third-party platforms you connect (advertising, payments,
          email, calendars), scores and summarizes that data using artificial-intelligence
          models, and surfaces tasks, briefings, and recommendations.
        </p>
      </Section>

      <Section heading="2. Eligibility and accounts">
        <p>
          You must be at least 18 years old and capable of entering a binding contract under the
          laws of your jurisdiction. You are responsible for maintaining the confidentiality of
          your login credentials and for all activity that occurs under your account.
        </p>
      </Section>

      <Section heading="3. Beta / pre-launch status">
        <p>
          The Service is in active development. Features, pricing, and behavior may change
          without notice. We may add, remove, or modify functionality at any time. While we will
          attempt to give reasonable notice of material changes, we make no commitment that the
          Service will be available without interruption.
        </p>
      </Section>

      <Section heading="4. Third-party connections">
        <p>
          You may choose to connect third-party platforms (for example, Facebook Ads, Stripe,
          Google) to Jarvis. Your use of those platforms is governed by their own terms.
          ClearFinOps is not responsible for the availability, accuracy, or behavior of any
          third-party platform. If a third party suspends, modifies, or terminates your account
          with them, your corresponding Jarvis functionality may also stop working.
        </p>
        <p>
          You authorize ClearFinOps to access, read, and process data from any third-party
          platform you connect, solely to provide the Service to you. You can revoke that
          authorization at any time from inside Jarvis or from the third-party platform.
        </p>
      </Section>

      <Section heading="5. AI-generated content">
        <p>
          Jarvis uses large-language-model providers to generate scores, summaries, and
          briefings. AI output may be inaccurate, incomplete, or misleading. You are responsible
          for reviewing AI-generated content before relying on it to make business decisions,
          send communications, or take any other action with material consequences.
        </p>
      </Section>

      <Section heading="6. Billing">
        <p>
          Paid plans are billed through Stripe. By subscribing you authorize ClearFinOps and
          Stripe to charge your chosen payment method on a recurring basis at the price quoted
          at sign-up. Subscriptions auto-renew until cancelled. You can cancel from the Billing
          page; cancellation takes effect at the end of the then-current billing period. Fees
          paid are non-refundable except where required by law.
        </p>
      </Section>

      <Section heading="7. Acceptable use">
        <p>You agree not to:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Use the Service for any unlawful purpose.</li>
          <li>
            Attempt to gain unauthorized access to the Service, other users&rsquo; accounts, or
            any underlying systems.
          </li>
          <li>
            Interfere with or disrupt the Service, including by sending automated requests at a
            rate that materially exceeds normal individual use.
          </li>
          <li>
            Reverse engineer, decompile, or otherwise attempt to derive the source code of the
            Service except where permitted by law.
          </li>
          <li>Use the Service to harass, defraud, or harm others.</li>
          <li>
            Connect a third-party account that you do not own or have explicit authorization to
            connect.
          </li>
        </ul>
      </Section>

      <Section heading="8. Ownership">
        <p>
          You retain all rights to the data you upload or connect (&ldquo;Customer Data&rdquo;).
          You grant ClearFinOps a non-exclusive, worldwide license to host, process, transmit,
          and display Customer Data solely to provide the Service to you. We retain all
          intellectual-property rights in the Service itself, including its software, design,
          and brand.
        </p>
      </Section>

      <Section heading="9. Confidentiality">
        <p>
          We will not disclose your Customer Data to third parties except (a) to subprocessors
          who help us run the Service under appropriate contractual protections, (b) when you
          direct or authorize disclosure, or (c) as required by law.
        </p>
      </Section>

      <Section heading="10. Disclaimer of warranties">
        <p className="uppercase text-xs tracking-wider">
          The Service is provided &ldquo;as is&rdquo; and &ldquo;as available,&rdquo; without
          warranty of any kind, whether express, implied, or statutory, including warranties of
          merchantability, fitness for a particular purpose, non-infringement, or that the
          Service will be uninterrupted or error-free. You use the Service at your own risk.
        </p>
      </Section>

      <Section heading="11. Limitation of liability">
        <p className="uppercase text-xs tracking-wider">
          To the maximum extent permitted by law, ClearFinOps LLC will not be liable for any
          indirect, incidental, special, consequential, or punitive damages, or for any loss of
          profits, revenue, data, or goodwill, arising out of or related to these Terms or the
          Service. Our total aggregate liability arising out of or related to these Terms or the
          Service will not exceed the amount you paid us in the twelve months immediately
          preceding the event giving rise to the liability, or one hundred U.S. dollars
          (USD&nbsp;$100), whichever is greater.
        </p>
      </Section>

      <Section heading="12. Indemnification">
        <p>
          You agree to indemnify and hold harmless ClearFinOps LLC and its officers, members,
          and employees from any third-party claim arising out of (a) your use of the Service,
          (b) your violation of these Terms, or (c) your violation of any law or the rights of
          a third party.
        </p>
      </Section>

      <Section heading="13. Termination">
        <p>
          You can stop using the Service at any time and may delete your account from the
          Settings page or by emailing us. We may suspend or terminate your access if you
          violate these Terms, if continued service to you would expose us to legal risk, or if
          we cease offering the Service. Sections that by their nature should survive
          termination (including Ownership, Disclaimer, Limitation of Liability, Indemnification,
          and Governing Law) will survive.
        </p>
      </Section>

      <Section heading="14. Governing law and disputes">
        <p>
          These Terms are governed by the laws of the State of Utah, USA, without regard to its
          conflict-of-laws principles. The state and federal courts located in Utah County, Utah
          have exclusive jurisdiction over any dispute arising out of or related to these Terms
          or the Service, and you consent to personal jurisdiction in those courts.
        </p>
      </Section>

      <Section heading="15. Changes to these Terms">
        <p>
          We may update these Terms from time to time. Material changes will be communicated
          through the Service or by email. Your continued use of the Service after a change
          takes effect constitutes acceptance of the updated Terms.
        </p>
      </Section>

      <Section heading="16. Contact">
        <p>
          Questions about these Terms? Email{' '}
          <a className="text-jarvis-blue hover:underline" href="mailto:kc@yourproject.ai">kc@yourproject.ai</a>{' '}
          or write to ClearFinOps LLC, Lehi, Utah, USA.
        </p>
      </Section>
    </LegalLayout>
    </>
  )
}
